import { Box, Divider, Typography } from "@mui/material";
import BatteryPower from "components/atomics/BatteryPower";
import DetailInfo from "components/atomics/DetailInfo";
import LoadingSpinner from "components/atomics/LoadingSpinner";
import ActionButton from "components/molecules/ActionButton";
import ImageCarousel, { CarouselImage } from "components/molecules/ImageCarousel";
import { BasicsTileWrapperSC, RightTileWrapperSC } from "components/pages/AssetManagement/tileStyles";
import PLTextlink from "components/patternLib/PLTextlink";
import {
  GetAssetMasterDataForAssetDetailsViewQuery,
  useGetAssetMasterDataForAssetDetailsViewQuery,
} from "graphqlBase/AssetMasterData/__generated__/getAssetMasterDataForAssetDetailsView";
import React, { useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom";
import translations from "translations";
import ImageGallery from "./ImageGallery";
import DeleteCommentModal from "./modals/DeleteCommentModal";
import EditCommentModal from "./modals/EditCommentModal";
import PhotoUploadButton from "./PhotoUploadButton";
import {
  BasicTileActionButtonWrapperSC,
  BatteryListItemSC,
  CommentBoxSC,
  DeviceInfoBoxSC,
  DeviceNameBoxSC,
  dividerStyles,
  imageDividerStyles,
  ListLabelSC,
  NoDeviceInfoBoxSC,
  topListBoxStyles,
} from "./styles";
import { getAssetInfo, getLastConnected } from "./utils";
import { useUpdateAssetDescriptionMutation } from "graphqlBase/Assets/__generated__/updateAssetDescription";

export type Asset = GetAssetMasterDataForAssetDetailsViewQuery["asset"];
interface AssetMasterDataTileProps {
  assetId: string;
}

export interface AssetImageCarousel extends CarouselImage {
  createdAt: string;
}
export interface FileWithPath extends File {
  readonly path?: string;
}
const AssetMasterDataTile: React.FC<AssetMasterDataTileProps> = ({ assetId }) => {
  const [deleteCommentModalOpen, setDeleteCommentModalOpen] = React.useState(false);
  const [editCommentModalOpen, setEditCommentModalOpen] = React.useState(false);
  const [commentValue, setCommentValue] = React.useState<string>("");
  const { battery: batteryLabel } = translations.entities.capability.capabilityNames;
  const history = useHistory();
  const { data, loading, refetch } = useGetAssetMasterDataForAssetDetailsViewQuery({
    variables: { id: assetId ?? "" },
    fetchPolicy: "cache-and-network",
  });
  const [updateAssetDescription] = useUpdateAssetDescriptionMutation();
  const assetType = data?.asset?.assetSource?.assetSourceName;
  const isCranePart = assetType?.includes("CRANE");
  const images = data?.asset?.assetImage?.reduce<Array<AssetImageCarousel>>((images, current) => {
    if (current?.pictureBlobStorageUrl)
      return images.concat({
        alt: current?.description ?? "",
        src: current?.pictureBlobStorageUrl,
        id: current?.id,
        createdAt: current.createdAt,
      });

    return images;
  }, []);

  const batteryVoltage = data?.asset?.assetDevice?.[0]?.device?.sensorMeasurementCurrentValues?.items?.find(
    (item) => item?.capabilityId === "80bec84b-2c50-4e12-9a70-56b06e5b92e5"
  )?.valueString;

  const batteryChargeStatus = data?.asset?.assetDevice?.[0]?.device?.sensorMeasurementCurrentValues?.items?.find(
    (item) => item?.capabilityId === "3c9e8f2a-1b4d-4e6f-9a8c-7d5b2e4a1c9f"
  )?.valueFloat;

  const assetInfo = getAssetInfo(data?.asset);
  const lastConnectedInfo = getLastConnected(data?.asset);
  const hasDevice = !!data?.asset?.assetDevice?.length;

  useEffect(() => {
    if (!data?.asset?.description) return;
    setCommentValue(data?.asset?.description);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.asset?.description]);

  const afterMutation = useCallback(async () => {
    if (refetch) await refetch();
  }, [refetch]);

  const handleUpdateComment = (comment?: string) => {
    const assetId = data?.asset?.id;
    if (!assetId) return;

    updateAssetDescription({ variables: { id: assetId, description: comment ?? "" } })
      .then(async () => {
        setDeleteCommentModalOpen(false);
        setEditCommentModalOpen(false);
        await afterMutation?.();
      })
      .catch((error) => console.debug("Error updating comment:", error));
  };

  return (
    <RightTileWrapperSC>
      <Typography sx={{ display: "flex" }} variant="h4">
        {translations.pages.assetManagement.assetDetails.assetMasterDataTile.label}
      </Typography>

      <LoadingSpinner sx={{ height: "fit-content" }} loading={loading}>
        <BasicsTileWrapperSC>
          {!isCranePart && images?.length ? (
            <>
              <ImageCarousel images={images} />
              <Divider variant="middle" sx={imageDividerStyles} />
            </>
          ) : null}
          <Box sx={topListBoxStyles}>
            <DetailInfo noSeparator data={assetInfo.concat(lastConnectedInfo)} itemHeight="35px" />
            {isCranePart && images?.length ? <ImageGallery images={images} comment={data?.asset?.description} /> : null}
            {isCranePart && <PhotoUploadButton assetId={assetId} afterMutation={afterMutation} />}
          </Box>
          <Divider variant="middle" sx={dividerStyles} />
          <CommentBoxSC>
            <DetailInfo
              noSeparator
              data={[
                {
                  label: translations.globals.terms.comment,
                  value: loading
                    ? ""
                    : data?.asset?.description?.trim()?.length
                    ? data?.asset?.description
                    : translations.globals.terms.noComment,
                },
              ]}
            />
            <BasicTileActionButtonWrapperSC>
              <ActionButton
                iconSize="1.5rem"
                fontSize="1rem"
                iconName="edit"
                label={translations.globals.button.edit}
                onClick={() => {
                  setCommentValue(data?.asset?.description ?? "");
                  setEditCommentModalOpen(true);
                }}
                disabled={!data?.asset?.id}
              />
              <ActionButton
                iconSize="1.5rem"
                fontSize="1rem"
                iconName="bin"
                label={translations.globals.button.delete}
                onClick={() => setDeleteCommentModalOpen(true)}
                disabled={!commentValue?.trim()?.length || !data?.asset?.id}
              />
            </BasicTileActionButtonWrapperSC>
          </CommentBoxSC>
          <Divider variant="middle" sx={dividerStyles} />
          <DeviceInfoBoxSC>
            {hasDevice ? (
              <>
                <DeviceNameBoxSC>
                  <ListLabelSC variant="copyBold">{translations.entities.device.name}</ListLabelSC>
                  <Box sx={{ flex: 1 }}>
                    {/*eslint-disable-next-line*/}
                    <PLTextlink
                      label={data?.asset?.assetDevice?.[0]?.device?.deviceName ?? ""}
                      onClick={() =>
                        history.push(`/deviceManagement/deviceDetails/${data?.asset?.assetDevice?.[0]?.device?.id}`)
                      }
                    />
                  </Box>
                </DeviceNameBoxSC>
                {data?.asset && (
                  <BatteryListItemSC>
                    <ListLabelSC>{batteryLabel}</ListLabelSC>
                    <BatteryPower batteryChargeStatus={batteryChargeStatus} batteryVoltage={batteryVoltage} />
                  </BatteryListItemSC>
                )}
              </>
            ) : (
              <NoDeviceInfoBoxSC>
                <Typography style={{ color: "#E52828" }}>{translations.pages.assetManagement.noDeviceText}</Typography>
              </NoDeviceInfoBoxSC>
            )}
          </DeviceInfoBoxSC>
        </BasicsTileWrapperSC>
      </LoadingSpinner>
      <DeleteCommentModal
        show={deleteCommentModalOpen}
        onConfirmDelete={() => {
          setCommentValue("");
          handleUpdateComment();
        }}
        onCancelDelete={() => setDeleteCommentModalOpen(false)}
      />
      <EditCommentModal
        show={editCommentModalOpen}
        onCancelEdit={() => setEditCommentModalOpen(false)}
        onChangeComment={(value) => setCommentValue(value)}
        onConfirmEdit={() => {
          handleUpdateComment(commentValue);
        }}
        commentValue={commentValue}
      />
    </RightTileWrapperSC>
  );
};
export default AssetMasterDataTile;
